import { ComponentChild } from 'preact'

import { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { Button } from '@bonniernews/dn-design-system-web/components/button/button.tsx'

interface TooltipProps extends SharedProps {
  /** Position of tooltip arrow */
  arrowPosition: 'top' | 'bottom'
  /** Title in tooltip */
  title?: string
  /** Show tooltip on render */
  forceOpen?: boolean
  /** Render close button if true */
  closeButton?: boolean
  /** Set to true to be able to close tooltip when clicking outside of it */
  closeOnClickOutside?: boolean
  /** Additional tooltip content */
  children?: ComponentChild
  /** Fixed pixel value is used for typography to prevent scaling based on html font-size */
  forcePx?: boolean
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/tooltip](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/tooltip)
 * - Storybook: [Tooltip](https://designsystem.dn.se/?path=/docs/basic-tooltip--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/tooltip/tooltip.scss'`
 */
export const Tooltip = ({
  arrowPosition,
  title,
  forceOpen = false,
  closeButton = false,
  closeOnClickOutside = false,
  children,
  forcePx = false,
  attributes = {},
  classNames,
}: TooltipProps) => {
  const componentClassName = 'ds-tooltip'

  const classes = formatClassString([
    componentClassName,
    `${componentClassName}--arrow-${arrowPosition}`,
    closeOnClickOutside && `${componentClassName}--close-outside-click`,
    !forceOpen && `${componentClassName}--hidden`,
    forcePx && `ds-force-px`,
    classNames,
  ])

  return (
    <div className={classes} {...attributes}>
      <div className={`${componentClassName}__content`}>
        {title && <h2>{title}</h2>}
        {children}
      </div>
      {closeButton && (
        <Button
          variant='transparent'
          size='sm'
          iconName='close'
          isIconButton={true}
          classNames={`${componentClassName}__close`}
          attributes={{ 'aria-label': 'Stäng' }}
        />
      )}
    </div>
  )
}
